import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import {
  forkSharedConversation,
  getSharedConversation,
  shareConversation,
  unshareConversation,
} from "../api/sharing";

/**
 * Create (or fetch the existing) public share link for a conversation. Re-syncs
 * the conversations namespace so the open thread's `share_token` updates.
 */
export function useShareConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shareConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
  });
}

/** Revoke a conversation's public share link (unshare). Re-syncs conversations. */
export function useUnshareConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unshareConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
  });
}

/**
 * The public, read-only shared conversation (no auth required). Disabled until a
 * token is present; never retries so an invalid token surfaces as "not found".
 */
export function useSharedConversation(token: string | null) {
  return useQuery({
    queryKey: queryKeys.sharing.detail(token ?? "__none__"),
    queryFn: () => getSharedConversation(token as string),
    enabled: token !== null,
    retry: false,
  });
}

/** Fork a shared conversation into the signed-in user's account. */
export function useForkSharedConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => forkSharedConversation(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
  });
}
